/* eslint-disable indent */

import type { MouseEvent as ReactMouseEvent } from "react";
import { Field } from "../util/graphFieldsExtractor";
import {
  Control,
  Controller,
  ControllerRenderProps,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { Checkbox, ColorField, Dropdown, Input } from "../../../common/components/Input";
import { FieldSelectOption } from "../util/FieldDescriptor";
import { IMedia } from "../../../model/unityObject";
import { MediaSelector } from "./MediaSelector";
import { useTranslation } from "react-i18next";
import { menuItems } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import { ICustomRemoveButton } from "src/common/components/CustomXIcon/CustomXIcon";
import { useScopeContext } from "./AssistantScenarIaRail/ScopeContext";
import InfoIconWithTooltip from "../../../common/components/Icon/InfoIconWithTooltip";

// Number fields whose unit is seconds. VR shows a slider (unit implicit); the
// web number input doesn't, so we clarify the (verbatim) label with a hover
// info-tooltip — distinct from the disabled/locked caption (Sébastien's ruling:
// tooltip for ambiguous names, greyed caption for disabled state).
const SECONDS_FIELDS = new Set([
  "time_code",
  "timer",
  "catch_time",
  "game_duration",
  "respond_timer",
  "time_begin",
]);

export const prettyLabel = (
  fieldName: string,
  t: (key: string, defaultValue?: any) => string,
): string => {
  const key = `pages.project.editNode.fields.${fieldName}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  const humanised = fieldName.replace(/_/g, " ");
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
};

/** Character counter matching the existing MediaNameInput / MediaDetail pattern. */
const CharCounter = ({
  control,
  name,
  maxLength,
  initialValue = "",
}: {
  control: Control<any>;
  name: string;
  maxLength: number;
  initialValue?: unknown;
}) => {
  const { t } = useTranslation();
  const value = useWatch({ control, name, defaultValue: initialValue as string });
  const len = String(value ?? "").length;
  const isNearLimit = len >= maxLength - 5;
  const isAtLimit = len >= maxLength;
  return (
    <div
      className={`text-sm mt-1 ${
        isAtLimit ? "text-red-600" : isNearLimit ? "text-yellow-600" : "text-gray-500"
      }`}
    >
      {len}/{maxLength} {t("general.characters") || "characters"}
    </div>
  );
};

export const DynamicField = ({
  field,
  register,
  control,
  disabled = false,
  className = undefined,
  staleWarning = false,
  hintText = undefined,
}: {
  field: Field;
  register: UseFormRegister<any>;
  control?: Control<any>;
  disabled?: boolean;
  className?: string;
  staleWarning?: boolean;
  // Small muted explanation rendered under the field (Phase 4): why a field is
  // locked (editable only in the source language) or, for the scene node, why a
  // conditional field is enabled/disabled (§2.D pt 3). Always rendered when
  // provided, so it never shifts layout.
  hintText?: string;
}) => {
  const { t } = useTranslation();
  const label = prettyLabel(field.descriptor?.labelKey ?? field.fieldName, t as any);
  const maxLength = field.descriptor?.jsonSchema?.maxLength as number | undefined;
  const { availableUnits, selectedUnitIds, isRailActive, isPickMode, isUnitSelected, toggleUnit } =
    useScopeContext();
  const scopeUnit = isRailActive
    ? availableUnits.find((u) => u.primaryField === field.fieldName)
    : null;
  const hasSelection = selectedUnitIds.length > 0;
  const fieldInScope = scopeUnit ? isUnitSelected(scopeUnit.id) : false;
  const dim = hasSelection && !fieldInScope;
  const highlight = hasSelection && fieldInScope;
  const wrapperIsScopeTarget = isPickMode && Boolean(scopeUnit);

  const counter =
    maxLength && control ? (
      <CharCounter
        control={control}
        name={field.fieldName}
        maxLength={maxLength}
        initialValue={field.currentValue}
      />
    ) : null;

  const renderField = () => {
    switch (field.type) {
      case "text":
        return (
          <>
            <Input
              registerFct={() => register(field.fieldName)}
              type="text"
              label={label}
              defaultValue={field.currentValue as any}
              disabled={disabled}
              className={className}
              maxLength={maxLength}
            />
            {counter}
          </>
        );
      case "longtext":
        return (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div className="mt-1">
              <textarea
                {...register(field.fieldName)}
                defaultValue={(field.currentValue as string) ?? ""}
                disabled={disabled}
                maxLength={maxLength}
                rows={4}
                className={`${
                  className ?? ""
                } appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
              />
            </div>
            {counter}
          </div>
        );
      case "boolean":
        return (
          // The shared inline Checkbox (box + label to its right) has no
          // label-above row, so in the 2-column field grid it floated at a
          // different baseline than sibling fields. An invisible spacer label
          // matching the sibling label row drops the checkbox onto the same
          // control row (mt-1), aligning it without changing the app-wide
          // inline-checkbox affordance.
          <div className="w-full">
            <label aria-hidden className="invisible block select-none text-sm font-medium text-gray-700">
              {label}
            </label>
            <div className="mt-1">
              <Checkbox
                id={`nodeform-bool-${field.fieldName}`}
                label={label}
                registerFct={() => register(field.fieldName)}
                defaultChecked={!!field.currentValue}
                disabled={disabled}
              />
            </div>
          </div>
        );
      case "arrayOfObjects":
        return (
          <div className="w-full opacity-50">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div className="mt-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-xs text-gray-500 italic">
              {t("pages.project.editNode.arrayFieldSpatialStudioHint")}
            </div>
          </div>
        );
      case "number": {
        // min/max/step come from the descriptor's jsonSchema (seeded from the VR
        // slider ranges, Appendix C); displayMode "percent" is DISPLAY-ONLY —
        // the widget shows value*100 but stores the raw float (Appendix C rule).
        const schema = field.descriptor?.jsonSchema ?? {};
        const isPercent = field.descriptor?.displayMode === "percent";
        const isInt = field.descriptor?.displayMode === "integer";
        const rawMin = typeof schema.minimum === "number" ? schema.minimum : undefined;
        const rawMax = typeof schema.maximum === "number" ? schema.maximum : undefined;
        const toDisplay = (raw: unknown): number | "" => {
          if (raw === "" || raw === null || raw === undefined) return "";
          const n = Number(raw);
          if (Number.isNaN(n)) return "";
          return isPercent ? Math.round(n * 100) : n;
        };
        const fromDisplay = (shown: string): number | null => {
          if (shown === "") return null;
          const n = Number(shown);
          if (Number.isNaN(n)) return null;
          return isPercent ? n / 100 : n;
        };
        const displayMin = isPercent && rawMin !== undefined ? rawMin * 100 : rawMin;
        const displayMax = isPercent && rawMax !== undefined ? rawMax * 100 : rawMax;
        const step = isPercent || isInt ? 1 : schema.multipleOf ?? "any";
        return (
          <Controller
            control={control}
            name={field.fieldName}
            defaultValue={field.currentValue ?? ""}
            render={({ field: rhf }) => (
              <div className="w-full">
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                  {label}
                  {SECONDS_FIELDS.has(field.fieldName) ? (
                    <InfoIconWithTooltip message={t("pages.project.editNode.secondsHint")} />
                  ) : null}
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    value={toDisplay(rhf.value)}
                    min={displayMin}
                    max={displayMax}
                    step={step}
                    disabled={disabled}
                    onChange={(e) => rhf.onChange(fromDisplay(e.target.value))}
                    onBlur={(e) => {
                      rhf.onBlur(); // notify RHF of blur (dirty / touched tracking)
                      // Clamp-on-blur: snap the stored value into [min, max] so an
                      // out-of-range typed entry never reaches the DB — mirrors the
                      // VR slider that cannot leave its track (§2.D pt 8;
                      // ElementCatchListField/NumberCell is the precedent pattern).
                      if (rawMin !== undefined || rawMax !== undefined) {
                        const raw = fromDisplay(e.target.value);
                        if (raw !== null) {
                          let clamped = raw;
                          if (rawMin !== undefined) clamped = Math.max(rawMin, clamped);
                          if (rawMax !== undefined) clamped = Math.min(rawMax, clamped);
                          if (isInt) clamped = Math.round(clamped);
                          if (Math.abs(clamped - raw) > 1e-10) rhf.onChange(clamped);
                        }
                      }
                    }}
                    className={`${
                      className ?? ""
                    } appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-50`}
                  />
                </div>
              </div>
            )}
          />
        );
      }
      case "select": {
        // Options come from the curated descriptor list, falling back to the
        // backend enum (jsonSchema.enum) when no curated list is supplied.
        const enumValues: unknown[] = Array.isArray(field.descriptor?.jsonSchema?.enum)
          ? field.descriptor?.jsonSchema?.enum
          : [];
        const options: FieldSelectOption[] =
          field.descriptor?.options ??
          enumValues.map((v) => ({ value: String(v), optionText: String(v) }));
        const currentStr =
          field.currentValue === null || field.currentValue === undefined
            ? ""
            : String(field.currentValue);
        const selectedIdx = options.findIndex((o) => o.value === currentStr);
        return (
          <Dropdown
            label={label}
            width="w-full"
            // Match the sibling fields' label size — Dropdown's default label is
            // text-lg, which dwarfs the text-sm labels of every other widget.
            labelFontClassName="text-sm"
            options={options}
            registerFct={() => register(field.fieldName)}
            disabled={disabled}
            // Preselect the stored value (skips the "please select" placeholder).
            // When the stored value isn't among the options, fall back to the
            // placeholder so the user is nudged to pick a valid one.
            asDefaultValue={selectedIdx >= 0}
            defaultValueIndex={selectedIdx}
          />
        );
      }
      case "color":
        return (
          <Controller
            control={control}
            name={field.fieldName}
            defaultValue={field.currentValue ?? ""}
            render={({ field: rhf }) => (
              <ColorField
                label={label}
                value={rhf.value as string}
                onChange={rhf.onChange}
                disabled={disabled}
                className={className}
              />
            )}
          />
        );
    }
    return <></>;
  };

  const rendered = (
    <>
      {renderField()}
      {hintText ? <p className="mt-1 text-xs italic text-gray-500">{hintText}</p> : null}
    </>
  );

  const wrapperBaseClass = "group relative w-full";
  const wrapperTransitionClass = dim ? "opacity-50 transition-opacity" : "transition-opacity";
  const ringClass = highlight ? "ring-2 ring-indigo-300 rounded-md p-1 -m-1" : "";
  const pickModeClass = wrapperIsScopeTarget
    ? "cursor-pointer ring-1 ring-dashed ring-indigo-200 rounded-md"
    : "";

  // In pick mode, the whole wrapper becomes the scope toggle target. Suppress
  // mouse-driven focus on nested inputs so clicking a textarea adds the unit
  // instead of dropping a caret; keyboard focus (Tab) still works.
  const scopeWrapperProps = wrapperIsScopeTarget
    ? {
        "data-scope-wrapper": "true" as const,
        role: "button" as const,
        tabIndex: -1,
        onMouseDownCapture: (e: ReactMouseEvent) => {
          e.preventDefault();
        },
        onClick: (e: ReactMouseEvent) => {
          if (!scopeUnit) return;
          e.stopPropagation();
          toggleUnit(scopeUnit.id);
        },
      }
    : {};

  const wrapperClassName =
    `${wrapperBaseClass} ${wrapperTransitionClass} ${ringClass} ${pickModeClass}`
      .replace(/\s+/g, " ")
      .trim();

  if (!staleWarning) {
    return (
      <div className={wrapperClassName} {...scopeWrapperProps}>
        {rendered}
      </div>
    );
  }

  return (
    <div className={wrapperClassName} {...scopeWrapperProps}>
      <div className="mb-1">
        <span className="badge-warning text-xs">
          {t("pages.project.editNode.staleTranslation")}
        </span>
      </div>
      {rendered}
    </div>
  );
};

export const DynamicMedia = ({
  fieldType,
  handleButtonClick,
  media,
  currentMedia,
  field,
  customMenuItems,
  disabled = false,
  CustomRemoveButton = undefined,
  className = undefined,
  showThreeDotsMenu = true,
}: {
  field: ControllerRenderProps;
  fieldType: string;
  register: UseFormRegister<any>;
  handleButtonClick: (mediaId: number, item: any, index: number) => void;
  media: IMedia;
  currentMedia: number | undefined;
  customMenuItems?: menuItems;
  disabled?: boolean;
  stopPlayingMedia?: boolean;
  CustomRemoveButton?: ICustomRemoveButton;
  className?: string;
  showThreeDotsMenu?: boolean;
}) => {
  const { t } = useTranslation();
  const med = media?.id
    ? media
    : ({
        createdAt: "",
        id: -1,
        name: `${t("pages.gallery.noMedia")}`,
        label: "",
        organization: 3,
        s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
        type: null,
        updatedAt: "",
        filename: "NOMEDIA.png",
        tags: [],
        upload_status: "completed",
      } as IMedia);
  const currentMed = Number(currentMedia) > 0 ? currentMedia : -1;
  switch (fieldType) {
    case "media":
      return (
        <MediaSelector
          field={field}
          handleButtonClick={handleButtonClick}
          media={med}
          currentMedia={currentMed}
          customMenuItems={customMenuItems}
          disabled={disabled}
          CustomRemoveButton={CustomRemoveButton}
          className={className}
          showThreeDotsMenu={showThreeDotsMenu}
        />
      );
  }
  return <></>;
};
